<script setup lang="ts">
import { effectiveUI } from '@/i18n'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
</script>

<template>
  <header class="header">
    <div class="header-inner">
      <!-- Language switcher - top on mobile, corner on desktop -->
      <div class="lang-corner">
        <LanguageSwitcher />
      </div>

      <div class="header-content">
        <div class="logo">
          <h1>{{ effectiveUI.title }}</h1>
        </div>
        <p class="subtitle">{{ effectiveUI.subtitle }}</p>
        <p v-if="effectiveUI.heroText" class="hero-text">{{ effectiveUI.heroText }}</p>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 2rem 0;
  text-align: center;
  position: relative;
  z-index: var(--z-header);
}

.header-inner {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
}

.lang-corner {
  position: absolute;
  /* Top-right corner (not vertically centered): popups open downward from
     the top edge instead of dropping into the middle of the title/subtitle.
     Positioned so it acts as the containing block for the mobile full-width
     dropdown sheet. */
  top: 1rem;
  right: 0;
}

.header-content {
  max-width: 650px;
  margin: 0 auto;
  transition: opacity var(--transition-normal);
}

/* While the language menu is open, fade the header text back so any overlap
   with the dropdown reads as a deliberate overlay, not clipped text.
   (Desktop the dropdown rarely touches the text; on narrow viewports it can,
   and this makes that look intentional.) */
.header:has(.dropdown) .header-content {
  opacity: 0.25;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.logo h1 {
  font-size: 1.75rem;
  margin: 0;
}

.subtitle {
  opacity: 0.9;
  margin: 0;
  font-size: 1rem;
}

.hero-text {
  opacity: 0.85;
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
  line-height: 1.5;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 640px) {
  .header {
    padding: 1rem 0 1.25rem;
  }

  .lang-corner {
    /* Positioned (not static) so the dropdown can anchor to this full-width
       row and span the viewport width as a menu sheet */
    position: relative;
    /* Neutralize the desktop corner offsets - relative positioning honors
       top/right, which would shift the row down over the title */
    top: auto;
    right: auto;
    z-index: var(--z-header);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .logo h1 {
    font-size: 1.35rem;
  }

  .subtitle {
    font-size: 0.8rem;
    padding: 0 0.5rem;
  }
}

@media (max-width: 350px) {
  .header {
    padding: 1rem 0;
  }
  
  .logo h1 {
    font-size: 1.15rem;
  }
}
</style>
